import type { ManagerCallParams, ManagerCallResult, OrganisationNode, OrganisationNodeRestParams } from '@sysarb/sysarb-types/services/v1/organisation-node';
import type { DisplayValueType } from '@/models/common/ValueTypes';
import type { UseMutationReturnType} from '@tanstack/vue-query';
import { useMutation, useQuery, type UseQueryReturnType } from '@tanstack/vue-query';
import { api } from '@/api';
import type { MaybeRefOrGetter } from 'vue';
import type { RemoveOrganisationManagerPayload, SetOrganisationManagerPayload, SetOrganisationDeputyManagerPayload, RemoveOrganisationDeputyManagerPayload, UpdateOrganisationNodePayload, SetActiveOrganisationNodePayload, DeleteNodeResult } from '@/api/OrganisationNodes';

type UseOrganisationNodesReturn = {
    // Queries
    organisationNodesState: UseQueryReturnType<OrganisationNode[] | null, Error>;
    possibleManagersState: UseQueryReturnType<ManagerCallResult[] | null, Error>;

    // Mutations
    setOrganisationManager: UseMutationReturnType<void | null, Error, SetOrganisationManagerPayload, unknown>;
    removeOrganisationManager: UseMutationReturnType<void | null, Error, RemoveOrganisationManagerPayload, unknown>;
    setOrganisationDeputyManager: UseMutationReturnType<void | null, Error, SetOrganisationDeputyManagerPayload, unknown>;
    removeOrganisationDeputyManager: UseMutationReturnType<void | null, Error, RemoveOrganisationDeputyManagerPayload, unknown>;
    updateOrganisationNode: UseMutationReturnType<void | null, Error, UpdateOrganisationNodePayload, unknown>;
    setActiveOrganisationNode: UseMutationReturnType<void | null, Error, SetActiveOrganisationNodePayload, unknown>;
    createOrganisationNode: UseMutationReturnType<OrganisationNode | null, Error, OrganisationNode, unknown>;
    deleteOrganisationNode: UseMutationReturnType<DeleteNodeResult | null, Error, string, unknown>;
    // Methods
    formatSelectedOrganisationNodes: (nodes: DisplayValueType<string | null>[]) => string;
};

type UseOrganisationNodesOptions = {
    initOrganisationNodes?: boolean;
    initPossibleManagers?: boolean;
    organisationNodesParams?: MaybeRefOrGetter<OrganisationNodeRestParams>
    possibleManagersParams?: MaybeRefOrGetter<ManagerCallParams>
};

export function useOrganisationNodes(options?: UseOrganisationNodesOptions): UseOrganisationNodesReturn {
    const _organisationNodesParams = options?.organisationNodesParams ?? { active: null, counted: false };
    const _possibleManagersParams = options?.possibleManagersParams ?? { Search: '', IsManager: undefined, HasManagerCode: undefined };

    const organisationNodesState = useQuery(api.organisationNodes.queries.getOrganisationNodes(_organisationNodesParams, options?.initOrganisationNodes));

    const possibleManagersState = useQuery(api.organisationNodes.queries.getPossibleManagers(_possibleManagersParams, options?.initPossibleManagers));

    const updateOrganisationNode = useMutation(api.organisationNodes.mutations.update);

    const setOrganisationManager = useMutation(api.organisationNodes.mutations.setOrganisationManager);

    const removeOrganisationManager = useMutation(api.organisationNodes.mutations.removeOrganisationManager);

    const setOrganisationDeputyManager = useMutation(api.organisationNodes.mutations.setOrganisationDeputyManager);

    const removeOrganisationDeputyManager = useMutation(api.organisationNodes.mutations.removeOrganisationDeputyManager);

    const setActiveOrganisationNode = useMutation(api.organisationNodes.mutations.setActive);

    const createOrganisationNode = useMutation(api.organisationNodes.mutations.create);

    const deleteOrganisationNode = useMutation(api.organisationNodes.mutations.delete);

    function formatSelectedOrganisationNodes(nodes: DisplayValueType<string | null>[]): string {
        if (nodes.length === 1) {
            return nodes[0].display;
        }
        let nodeStringList = '';
        nodes.forEach(node => {
            nodeStringList += `${node.display}, `;
        });
        return nodeStringList;
    }

    return {
        organisationNodesState,
        formatSelectedOrganisationNodes,
        possibleManagersState,
        setOrganisationManager,
        removeOrganisationManager,
        setOrganisationDeputyManager,
        removeOrganisationDeputyManager,
        updateOrganisationNode,
        setActiveOrganisationNode,
        createOrganisationNode,
        deleteOrganisationNode
    };
}
